import React, { useState } from "react";
import { ArrowRight, ArrowUpRight, ChevronDown,  MessageCircle, ShoppingBag, ScanLine } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  ONE component, everything self-contained: styles, data, markup.   */
/*  Signature element: the laminated "Official Member ID Card".       */
/* ------------------------------------------------------------------ */

const PILLARS = [
  { n: "01", title: "Transparent Governance", body: "Every decision documented and made public. No closed-door meetings on matters that affect the community." },
  { n: "02", title: "Equal Representation", body: "Every member gets an equal voice, regardless of background or status. One person, one vote." },
  { n: "03", title: "Community First", body: "Resources follow community need, not convenience. Local chapters run their own local matters." },
  { n: "04", title: "Accountability Standards", body: "Representatives publish quarterly reports. Missed commitments trigger a membership review." },
  { n: "05", title: "Open Membership", body: "Anyone who shares the values can join — no barrier of profession, education, or standing." },
];

const ELIGIBILITY = [
  { title: "Concerned Citizen", body: "You care about your community and want real change." },
  { title: "Motivated", body: "Ready to give your time and energy to the cause." },
  { title: "Connected", body: "Able to keep up with online discussions and updates." },
  { title: "Passionate", body: "Can articulate your views clearly and respectfully." },
];

const MERCH = [
  { name: "Printed Tee — Black", price: 499, mrp: 799 },
  { name: "Printed Tee — Black V2", price: 499, mrp: 799 },
  { name: "Printed Tee — Black V3", price: 499, mrp: 799 },
];

const FAQS = [
  { q: "What is the Cockroach Janta Party?", a: "A viral youth movement and political satire on governance and corruption, grown into a digital activism platform demanding transparency and reform." },
  { q: "Why the name 'Cockroach'?", a: "Cockroaches survive against all odds — a pointed, humorous stand-in for the resilience of the common citizen demanding change." },
  { q: "Who can join?", a: "Anyone 18 or older who believes in transparency, accountability, and democratic participation." },
  { q: "How much does membership cost?", a: "It's completely free. Activism shouldn't have a paywall." },
];

export default function CJPMemberCardSite() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div style={styles.root}>
      <style>{css}</style>

      {/* SALE RIBBON */}
      <div className="ribbon">
        <ShoppingBag size={13} /> Sale live — limited time · 37% off merch <a href="#merch">Shop now <ArrowUpRight size={12} /></a>
      </div>

      {/* MARQUEE */}
      <div className="marquee">
        <div className="marquee-track">
          {Array(2).fill(0).map((_, i) => (
            <span key={i}>BREAKING NEWS &nbsp;•&nbsp; JOIN THE MOVEMENT &nbsp;•&nbsp; MAKE YOUR VOICE HEARD &nbsp;•&nbsp; TOGETHER WE RISE &nbsp;•&nbsp; </span>
          ))}
        </div>
      </div>

      {/* NAV */}
      <header className="nav">
        <div className="wrap nav-row">
          <div className="brand">
            <div className="brand-mark">कॉ</div>
            <div className="brand-text">Cockroach Janta Party<span>Delhi Chapter · Est. 2026</span></div>
          </div>
          <nav className="nav-links">
            <a href="#vision">Vision</a><a href="#manifesto">Manifesto</a><a href="#eligibility">Eligibility</a><a href="#contact">Contact</a>
          </nav>
          <a className="btn-gold" href="#contact">Join the Party</a>
        </div>
      </header>

      {/* HERO */}
      <section className="hero wrap">
        <div className="hero-copy">
          <div className="eyebrow">CJP Official · Delhi Chapter</div>
          <h1>Voice of the Lazy<br/>&amp; <em>Unemployed.</em></h1>
          <p>
            India's viral youth political satire and reform movement. Demand transparency,
            demand accountability, demand real change. Membership is free for every Indian.
          </p>
          <div className="hero-actions">
            <a className="btn-gold" href="#contact">Join the Party <ArrowRight size={14} /></a>
            <a className="btn-line" href="#manifesto">Read the Manifesto</a>
          </div>
          <div className="hero-stats">
            <div><b>5</b><span>Members</span></div>
            <div><b>0</b><span>Donations</span></div>
            <div><b>1</b><span>Chapter</span></div>
          </div>
        </div>

        {/* SIGNATURE: Member ID Card */}
        <div className="card-stage">
          <div className="id-card">
            <div className="id-card-sheen" />
            <div className="id-card-top">
              <div className="id-card-mark">कॉ</div>
              <div className="id-card-org">COCKROACH<br/>JANTA PARTY<span>Delhi Chapter</span></div>
            </div>
            <div className="id-card-photo">
              <ScanLine size={26} />
            </div>
            <div className="id-card-name">Together We Rise</div>
            <div className="id-card-quote">"They said we couldn't.<br/>We came back."</div>
            <div className="id-card-bottom">
              <div>
                <span>Member</span>
                <b>#00001</b>
              </div>
              <div className="barcode" aria-hidden="true" />
            </div>
            <div className="id-card-stamp">APPROVED</div>
          </div>
        </div>
      </section>

      {/* MERCH */}
      <section className="section wrap" id="merch">
        <div className="section-head">
          <div className="eyebrow">Official Merchandise</div>
          <h2>Wear the movement.</h2>
        </div>
        <div className="merch-grid">
          {MERCH.map((m, i) => (
            <div className="merch-card" key={i}>
              <div className="merch-tag">37% OFF</div>
              <div className="merch-swatch" />
              <div className="merch-name">{m.name}</div>
              <div className="merch-price"><b>₹{m.price}</b><s>₹{m.mrp}</s></div>
              <a className="btn-line small" href="#merch">Buy Now</a>
            </div>
          ))}
        </div>
      </section>

      {/* VISION */}
      <section className="section wrap" id="vision">
        <div className="vision-grid">
          <div>
            <div className="eyebrow">Chapter One</div>
            <h2>Our movement's vision.</h2>
            <p>We're here to represent the everyday citizen — asking hard questions and demanding real answers. No corporate interest. No hidden agenda. Just honest representation.</p>
          </div>
          <div className="vision-quote">
            "Building a community for those who believe in change through unity, transparency, and collective action."
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="section wrap" id="manifesto">
        <div className="section-head">
          <div className="eyebrow">The Five Pillars</div>
          <h2>The manifesto.</h2>
        </div>
        <div className="pillar-grid">
          {PILLARS.map(p => (
            <div className="pillar" key={p.n}>
              <span className="pillar-num">{p.n}</span>
              <h4>{p.title}</h4>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="section wrap" id="eligibility">
        <div className="section-head">
          <div className="eyebrow">Membership</div>
          <h2>Are you eligible to join?</h2>
        </div>
        <div className="elig-grid">
          {ELIGIBILITY.map((e, i) => (
            <div className="elig-card" key={i}>
              <div className="elig-yn">Yes / No</div>
              <h4>{e.title}</h4>
              <p>{e.body}</p>
            </div>
          ))}
        </div>
        <p className="fineprint">Membership is free. Nothing compulsory. Just you, the form, and the politics of the people.</p>
      </section>

      {/* FAQ */}
      <section className="section wrap" id="faq">
        <div className="section-head">
          <div className="eyebrow">Frequently Asked</div>
          <h2>Questions, answered.</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((f, i) => (
            <div className={`faq-item ${openFaq === i ? "open" : ""}`} key={i}>
              <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                {f.q}
                <ChevronDown size={16} className="chev" />
              </button>
              {openFaq === i && <div className="faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section className="section wrap" id="contact">
        <div className="section-head">
          <div className="eyebrow">Get In Touch</div>
          <h2>Connect with us.</h2>
        </div>
        <div className="contact-grid">
          <p>Want to join, volunteer, contribute, or send a message? We read everything, we reply to most things.</p>
          <form onSubmit={e => e.preventDefault()} className="contact-form">
            <input className="field" placeholder="Full Name" />
            <input className="field" placeholder="Email or Phone" />
            <textarea className="field" placeholder="Message" rows={3} />
            <button className="btn-gold" type="submit">Send &amp; Join <ArrowRight size={14} /></button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer wrap">
        <div className="footer-row">
          <div className="brand">
            <div className="brand-mark small">कॉ</div>
            <div className="brand-text small">Cockroach Janta Party Delhi</div>
          </div>
          <div className="socials">
            <a href="#"><MessageCircle size={15} /></a>
          </div>
        </div>
        <div className="footer-bottom">© 2024 Cockrocha Janta Party Delhi · All rights reserved</div>
      </footer>
    </div>
  );
}

const styles = {
  root: {
    "--bg": "#170d0d",
    "--surface": "#221313",
    "--card": "#f6efdd",
    "--gold": "#d4af37",
    "--crimson": "#c81d25",
    "--ink": "#241414",
    "--paper": "#f3ece0",
    "--muted": "#a9948a",
    "--line": "#3a2323",
    fontFamily: "'Poppins', sans-serif",
    background: "var(--bg)",
    color: "var(--paper)",
    minHeight: "100vh",
  },
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=Poppins:wght@400;500;600;700;800&display=swap');

* { box-sizing: border-box; }
.wrap { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
a { text-decoration: none; color: inherit; }
button { font-family: inherit; cursor: pointer; }
h1,h2,h3,h4 { font-family: 'Playfair Display', serif; margin: 0; }
p { margin: 0; }

.eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: .16em; color: var(--gold); font-weight: 600; margin-bottom: 10px; }

/* RIBBON */
.ribbon {
  background: var(--crimson); color: #fff; font-size: 12.5px; font-weight: 600;
  padding: 9px 20px; display: flex; align-items: center; justify-content: center; gap: 8px; text-align: center;
}
.ribbon a { text-decoration: underline; display: inline-flex; align-items: center; gap: 3px; margin-left: 4px; }

/* MARQUEE */
.marquee { background: var(--surface); border-bottom: 1px solid var(--line); overflow: hidden; }
.marquee-track { display: flex; white-space: nowrap; animation: scroll 22s linear infinite; padding: 7px 0; font-size: 11px; letter-spacing: .08em; color: var(--muted); text-transform: uppercase; }
@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }

/* NAV */
.nav { position: sticky; top: 0; z-index: 30; background: rgba(23,13,13,0.92); backdrop-filter: blur(6px); border-bottom: 1px solid var(--line); }
.nav-row { display: flex; align-items: center; justify-content: space-between; height: 72px; }
.brand { display: flex; align-items: center; gap: 12px; }
.brand-mark { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, var(--gold), #8a6d1f); display: flex; align-items: center; justify-content: center; color: var(--ink); font-weight: 800; font-size: 15px; font-family: 'Playfair Display', serif; }
.brand-mark.small { width: 30px; height: 30px; font-size: 12px; }
.brand-text { font-family: 'Playfair Display', serif; font-weight: 800; font-size: 16px; line-height: 1.2; }
.brand-text span { display: block; font-family: 'Poppins'; font-weight: 400; font-size: 10px; color: var(--muted); letter-spacing: .06em; margin-top: 2px; }
.brand-text.small { font-size: 13px; }
.nav-links { display: flex; gap: 30px; font-size: 13px; font-weight: 500; }
.nav-links a { color: var(--muted); transition: color .2s; }
.nav-links a:hover { color: var(--gold); }
.btn-gold { background: linear-gradient(135deg, var(--gold), #a9841f); color: var(--ink); font-weight: 700; font-size: 13px; padding: 11px 20px; border-radius: 6px; display: inline-flex; align-items: center; gap: 6px; border: none; }
.btn-line { border: 1px solid var(--line); color: var(--paper); font-size: 13px; font-weight: 500; padding: 10px 20px; border-radius: 6px; display: inline-flex; align-items: center; gap: 6px; }
.btn-line:hover { border-color: var(--gold); color: var(--gold); }
.btn-line.small { padding: 8px 14px; font-size: 12px; width: 100%; justify-content: center; margin-top: 14px; }

/* HERO */
.hero { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 50px; align-items: center; padding: 70px 24px 90px; }
.hero h1 { font-size: clamp(38px, 5vw, 58px); line-height: 1.08; font-weight: 800; }
.hero h1 em { font-style: normal; color: var(--gold); }
.hero-copy p { color: var(--muted); font-size: 14.5px; line-height: 1.7; margin: 22px 0 30px; max-width: 460px; }
.hero-actions { display: flex; gap: 12px; flex-wrap: wrap; }
.hero-stats { display: flex; gap: 40px; margin-top: 44px; }
.hero-stats b { display: block; font-family: 'Playfair Display', serif; font-size: 30px; color: var(--gold); }
.hero-stats span { font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: var(--muted); }

/* MEMBER CARD */
.card-stage { display: flex; justify-content: center; perspective: 1200px; }
.id-card {
  width: 320px; min-height: 400px; border-radius: 18px; padding: 26px 24px;
  background: linear-gradient(155deg, var(--card), #e9dfc2);
  color: var(--ink); position: relative; overflow: hidden;
  box-shadow: 0 30px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.4);
  transform: rotate(4deg);
  transition: transform .4s ease;
}
.id-card:hover { transform: rotate(0deg) scale(1.02); }
.id-card-sheen {
  position: absolute; top: -60%; left: -30%; width: 60%; height: 220%;
  background: linear-gradient(75deg, transparent, rgba(255,255,255,0.5), transparent);
  transform: rotate(20deg);
}
.id-card-top { display: flex; align-items: center; gap: 10px; margin-bottom: 22px; }
.id-card-mark { width: 34px; height: 34px; border-radius: 50%; background: var(--crimson); color: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Playfair Display'; font-weight: 800; font-size: 13px; flex-shrink: 0; }
.id-card-org { font-family: 'Playfair Display'; font-weight: 800; font-size: 12px; line-height: 1.2; letter-spacing: .02em; }
.id-card-org span { display: block; font-family: 'Poppins'; font-weight: 500; font-size: 9px; color: #6b5f4a; margin-top: 2px; }
.id-card-photo { width: 100%; height: 130px; border-radius: 10px; background: repeating-linear-gradient(135deg, #d8cca4, #d8cca4 8px, #cfc198 8px, #cfc198 16px); display: flex; align-items: center; justify-content: center; color: #6b5f4a; margin-bottom: 16px; }
.id-card-name { font-family: 'Playfair Display'; font-weight: 800; font-size: 19px; margin-bottom: 6px; }
.id-card-quote { font-size: 11.5px; color: #5c5240; line-height: 1.5; font-style: italic; margin-bottom: 18px; }
.id-card-bottom { display: flex; justify-content: space-between; align-items: flex-end; border-top: 1px dashed #b8a878; padding-top: 14px; }
.id-card-bottom span { display: block; font-size: 9px; text-transform: uppercase; letter-spacing: .1em; color: #7a6d54; }
.id-card-bottom b { font-family: 'Playfair Display'; font-size: 16px; }
.barcode { width: 90px; height: 26px; background: repeating-linear-gradient(90deg, #241414 0 2px, transparent 2px 5px); }
.id-card-stamp {
  position: absolute; bottom: 22px; right: -34px; background: var(--crimson); color: #fff;
  font-size: 9px; font-weight: 700; letter-spacing: .1em; padding: 4px 40px; transform: rotate(-38deg);
  opacity: 0; pointer-events: none;
}
.id-card:hover .id-card-stamp { opacity: 0.92; }

/* SECTION shell */
.section { padding: 80px 0; border-bottom: 1px solid var(--line); }
.section-head { margin-bottom: 40px; }
.section-head h2 { font-size: clamp(28px, 3.6vw, 40px); font-weight: 800; }

/* MERCH */
.merch-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.merch-card { background: var(--surface); border: 1px solid var(--line); border-radius: 12px; padding: 18px; position: relative; }
.merch-tag { position: absolute; top: 14px; left: 14px; background: var(--crimson); color: #fff; font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 4px; }
.merch-swatch { height: 160px; border-radius: 8px; background: linear-gradient(135deg, #2b2b2b, #111); margin-bottom: 14px; }
.merch-name { font-size: 13.5px; font-weight: 600; margin-bottom: 8px; }
.merch-price b { font-family: 'Playfair Display'; font-size: 18px; color: var(--gold); margin-right: 8px; }
.merch-price s { font-size: 12px; color: var(--muted); }

/* VISION */
.vision-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 50px; align-items: center; }
.vision-grid h2 { font-size: clamp(26px, 3.4vw, 36px); font-weight: 800; margin: 6px 0 18px; }
.vision-grid p { color: var(--muted); font-size: 14px; line-height: 1.8; }
.vision-quote { font-family: 'Playfair Display'; font-size: 24px; font-weight: 700; line-height: 1.4; color: var(--gold); border-left: 3px solid var(--gold); padding-left: 24px; }

/* MANIFESTO */
.pillar-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.pillar { background: var(--surface); border: 1px solid var(--line); border-radius: 12px; padding: 24px; }
.pillar-num { font-family: 'Playfair Display'; font-size: 26px; color: var(--gold); font-weight: 800; }
.pillar h4 { font-size: 16px; margin: 12px 0 8px; font-weight: 700; }
.pillar p { font-size: 12.5px; color: var(--muted); line-height: 1.6; }

/* ELIGIBILITY */
.elig-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.elig-card { border: 1px solid var(--line); border-radius: 12px; padding: 22px; text-align: center; }
.elig-yn { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: var(--crimson); font-weight: 700; margin-bottom: 12px; }
.elig-card h4 { font-size: 15px; margin-bottom: 8px; font-weight: 700; }
.elig-card p { font-size: 12px; color: var(--muted); line-height: 1.55; }
.fineprint { text-align: center; color: var(--muted); font-size: 12.5px; margin-top: 28px; }

/* FAQ */
.faq-list { display: flex; flex-direction: column; gap: 0; }
.faq-item { border-bottom: 1px solid var(--line); }
.faq-q { width: 100%; background: none; border: none; color: var(--paper); font-size: 14.5px; font-weight: 600; padding: 18px 0; display: flex; justify-content: space-between; align-items: center; text-align: left; }
.faq-item.open .chev { transform: rotate(180deg); }
.chev { transition: transform .2s; color: var(--gold); }
.faq-a { color: var(--muted); font-size: 13px; line-height: 1.7; padding-bottom: 18px; max-width: 640px; }

/* CONTACT */
.contact-grid { display: grid; grid-template-columns: 0.8fr 1.2fr; gap: 50px; align-items: start; }
.contact-grid > p { color: var(--muted); font-size: 14px; line-height: 1.7; }
.contact-form { display: flex; flex-direction: column; gap: 14px; }
.field { background: var(--surface); border: 1px solid var(--line); border-radius: 8px; padding: 13px 14px; color: var(--paper); font-family: inherit; font-size: 13px; outline: none; }
.field:focus { border-color: var(--gold); }

/* FOOTER */
.footer { padding: 40px 0 30px; }
.footer-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.socials { display: flex; gap: 10px; }
.socials a { width: 32px; height: 32px; border: 1px solid var(--line); border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.socials a:hover { border-color: var(--gold); color: var(--gold); }
.footer-bottom { font-size: 11px; color: var(--muted); border-top: 1px solid var(--line); padding-top: 18px; }

@media (max-width: 860px) {
  .hero { grid-template-columns: 1fr; }
  .card-stage { margin-top: 20px; }
  .merch-grid, .pillar-grid { grid-template-columns: 1fr 1fr; }
  .elig-grid { grid-template-columns: 1fr 1fr; }
  .vision-grid, .contact-grid { grid-template-columns: 1fr; }
  .nav-links { display: none; }
}
@media (max-width: 560px) {
  .merch-grid, .pillar-grid, .elig-grid { grid-template-columns: 1fr; }
}
`;
